import { inject, injectable } from 'tsyringe';
import { BaseViewModel } from '@/shared/viewmodels/BaseViewModel';
import { IAnalyticsGatewayToken, type IAnalyticsGateway } from '@/shared/analytics/AnalyticsGateway';
import type { AnalyticsFilter } from '@/shared/analytics/AnalyticsTypes';
import { RuntimeConfigToken, type RuntimeConfig } from '@/runtime-config/RuntimeConfig';
import { GlobalFilterController } from '@/features/global-filters/GlobalFilterController';
import { resolveDateRange, resolveWindowDays } from '@/features/global-filters/GlobalFilterTypes';
import { makeRng } from './SignalSampleData';
import { resolveSignalTuning, type SignalTuning } from '@/runtime-config/SignalTuning';
import { ISignalLicenseSourceToken, type ISignalLicenseSource, type SignalContext, type SignalData, type SignalTileDef } from './SignalTypes';

/**
 * Drives a surface's signal grid. On {@link load} it synchronously seeds every
 * tile with its deterministic sample (so the page paints instantly and never
 * jumps), then fires each tile's optional `live` loader; a resolved loader
 * replaces that tile's data and flips it to "live", while a failure silently
 * keeps the sample. Loads are keyed by tile set + ambient filters, so the grid
 * re-queries when filters change but ignores redundant re-renders.
 */
@injectable()
export class SurfaceSignalsViewModel extends BaseViewModel {
    data: Record<string, SignalData> = {};
    private liveIds = new Set<string>();
    private loadingIds = new Set<string>();
    private key: string | null = null;

    private readonly tuning: SignalTuning;

    constructor(
        @inject(IAnalyticsGatewayToken) private readonly gateway: IAnalyticsGateway,
        @inject(ISignalLicenseSourceToken) private readonly licenses: ISignalLicenseSource,
        @inject(GlobalFilterController) private readonly globalFilters: GlobalFilterController,
        @inject(RuntimeConfigToken) runtimeConfig: RuntimeConfig,
    ) {
        super();
        this.tuning = resolveSignalTuning(runtimeConfig);
    }

    isLive(id: string): boolean {
        return this.liveIds.has(id);
    }

    isLoading(id: string): boolean {
        return this.loadingIds.has(id);
    }

    load(defs: SignalTileDef[], filters: AnalyticsFilter[], scopeKey: string = ''): void {
        const key = `${defs.map((def) => def.id).join(',')}::${serializeFilters(filters)}::${scopeKey}`;
        if (this.key === key) return;
        this.key = key;

        const seeded: Record<string, SignalData> = {};
        for (const def of defs) seeded[def.id] = def.sample(makeRng(def.id));
        this.data = seeded;
        this.liveIds = new Set();
        this.loadingIds = new Set(defs.filter((def) => def.live !== undefined).map((def) => def.id));
        this.notifyChange();

        const dateRange = this.globalFilters.snapshot().dateRange;
        const { start, end } = resolveDateRange(dateRange);
        const ctx: SignalContext = {
            gateway: this.gateway,
            licenses: this.licenses,
            filters,
            tuning: this.tuning,
            range: { start: start.toISOString(), end: end.toISOString() },
            windowDays: resolveWindowDays(dateRange),
        };
        for (const def of defs) {
            if (def.live === undefined) continue;
            void def
                .live(ctx)
                .then((result) => {
                    if (this.key !== key) return;
                    this.data = { ...this.data, [def.id]: result };
                    this.liveIds.add(def.id);
                })
                .catch((cause: unknown) => {
                    console.debug('[signals] live load failed', def.id, cause);
                })
                .finally(() => {
                    if (this.key !== key) return;
                    this.loadingIds.delete(def.id);
                    this.notifyChange();
                });
        }
    }
}

function serializeFilters(filters: AnalyticsFilter[]): string {
    return filters
        .map((filter) => `${filter.field}=${[...filter.values].sort().join('|')}`)
        .sort()
        .join(';');
}
